import { get, post } from "@/services/http/platformServer";
import { PagedResult } from "../types";
import LogisticsCompany from "@/enum/LogisticsCompany";
import OrderSellerRemarksDTO from "@/dto/order/OrderSellerRemarksDTO";

export interface TaobaoReshippingDTO {
    /**
     * 买家地址
     */
    buyerAddress?: string;
    /**
     * 买家昵称
     */
    buyerNick?: string;

    /**
     * 买家姓名
     */
    buyerName: string;

    /**
     * 买家OpenUID
     */
    buyerOpenUid?: string;
    /**
     * 买家手机号
     */
    buyerPhone?: string;
    /**
     * 创建时间
     */
    created?: string;
    /**
     * 买家留言
     * 需要另外调用补寄详情接口才能获取，暂时不返回
     */
    desc?: string;
    /**
     * 补寄单ID
     */
    disputeId?: number;
    /**
     * 商品状态
     */
    goodStatus?: string;
    /**
     * 商品数量
     */
    num?: number;
    /**
     * 订单信息
     */
    order?: OrderInfo;
    /**
     * 收货人昵称
     */
    realReceiverDisplayNick?: string;
    /**
     * 补寄原因
     */
    reason?: string;
    /**
     * 超时时间
     */
    timeOut?: string;

    refundPhase?: TaobaoReshippingRefundPhase;

    /** 发货物流公司 */
    sellerLogisticName?: string;

    /** 卖家发货快递单号 */
    sellerLogisticNo?: string;

    /** 前端值 */
    shopId?: number;
    status?: ReshippingStatus;
}

export enum TaobaoReshippingRefundPhase {
    onsale = "onsale",
    aftersale = "aftersale",
}

export const TaobaoReshippingRefundPhaseNameMap = {
    [TaobaoReshippingRefundPhase.onsale]: "售中",
    [TaobaoReshippingRefundPhase.aftersale]: "售后",
};

interface OrderInfo {
    /**
     * 订单商品信息
     */
    orderItem?: OrderItemInfo;
    /**
     * 下单时间
     */
    orderTime?: string;
    /**
     * 订单编号
     */
    originalId?: string;
    /**
     * 支付时间
     */
    paymentTime?: string;

    sellerRemarks: OrderSellerRemarksDTO;
}

export interface OrderItemInfo {
    /**
     * 商品金额
     */
    amounts: number;
    /**
     * 商品图片URL
     */
    picture?: string;
    /**
     * 商品单价
     */
    price: number;
    /**
     * 商品平台ID
     */
    productOriginalId?: string;
    /**
     * 商品数量
     */
    quantity: number;
    /**
     * SKU平台ID
     */
    skuOriginalId?: string;
    /**
     * SKU属性
     */
    skuProperties?: string;
    /**
     * 商品标题
     */
    title?: string;
}

export enum ReshippingStatus {
    /** 补寄待处理 */
    WAIT_SELLER_AGREE = "WAIT_SELLER_AGREE",
    /** 等待卖家发货 */
    WAIT_SELLER_SEND_GOODS = "WAIT_SELLER_SEND_GOODS",
    /** 等待买家收货 */
    WAIT_BUYER_CONFIRM_REDO_SEND_GOODS = "WAIT_BUYER_CONFIRM_REDO_SEND_GOODS",
    /** 补寄成功 */
    SUCCESS = "SUCCESS",
    /** 卖家拒绝补寄 */
    SELLER_REFUSE_BUYER = "SELLER_REFUSE_BUYER",
    /** 补寄关闭 */
    CLOSED = "CLOSED",
    /** 转退款 */
    EXCHANGE_TRANSFORM_TO_REFUND = "EXCHANGE_TRANSFORM_TO_REFUND",
}

interface GetReshippingListParams {
    shopId: number;
    status: ReshippingStatus;
    pageNo: number;
    pageSize: number;
    disputeIds: string;
    orderOriginalId: string;
    startTime: string;
    endTime: string;
}

/** 获取补寄单列表 */
export async function getReshippingList(params: GetReshippingListParams) {
    return get<PagedResult<TaobaoReshippingDTO>>(params.shopId, "/taobao/reshipping/list", params).then((res) => {
        res.items?.forEach((it) => {
            it.shopId = params.shopId;
            it.status = params.status;
        });

        return res;
    });
}

/** 同意补寄 */
export function agreeReshipping(shopId: number, disputeId: number) {
    return post<void>(shopId, "/taobao/reshipping/agree", {
        shopId,
        disputeId,
    });
}

/** 获取补寄拒绝原因 */
export function getReshippingRefuseReasons(shopId: number, disputeId: number) {
    return get<{ id: number; content: string }[]>(shopId, "/taobao/reshipping/refuse-reasons", {
        shopId,
        disputeId,
    });
}

/** 拒绝补寄 */
export function refuseReshipping(shopId: number, disputeId: number, refuseReasonId: number, message: string) {
    return post<void>(shopId, "/taobao/reshipping/refuse", {
        shopId,
        disputeId,
        refuseReasonId,
        message,
    });
}

/** 补寄发货 */
export function shipReshipping(
    shopId: number,
    disputeId: number,
    logisticsCompany: LogisticsCompany,
    waybillNo: string,
) {
    return post<void>(shopId, "/taobao/reshipping/consign", {
        shopId,
        disputeId,
        logisticsCompany,
        waybillNo,
    });
}

export interface TaobaoReshippingNegotiationDTO {
    /**
     * 留言内容
     */
    content?: string;
    /**
     * 留言时间
     */
    created?: string;
    /**
     * 留言类型
     */
    messageType?: string;
    /**
     * 留言人昵称
     */
    ownerNick?: string;
    /**
     * 留言人角色
     */
    ownerRole?: string;
    /**
     * 凭证图片url
     */
    picUrls?: string[];
}

/** 获取补寄协商信息 */
export function getReshippingNegotiationInfo(shopId: number, disputeId: number) {
    return get<TaobaoReshippingNegotiationDTO[]>(shopId, "/taobao/reshipping/negotiations", {
        shopId,
        disputeId,
    });
}
